// Imports for external libraries go here.
import React, { FC, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { EditableComponent, ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { useDCAPageModelStore } from '@marriott/mi-store-utils';
import { respGridUtil } from '@marriott/mi-headless-utils';

import { IMerchandisingDCAContainerProps, IUpdatedPageModel } from './MerchandisingDCAContainer.types';
import { StyledMerchandisingDCAContainer } from './MerchandisingDCAContainer.styles';

const DCAContainer: FC<IMerchandisingDCAContainerProps> = props => {
  const {
    isAuthorMode,
    pagePath,
    itemPath,
    cqItemsOrder,
    cqItems,
    isTargetContentLoaded,
    enableOfferVariation = false,
    styleclass = '',
    hideByDefault = false,
  } = props;

  const AuthorComponents = () => {
    return (
      <>
        {Array.from({ length: 1 }, (_, index) => (
          <div>
            <ResponsiveGrid
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              pagePath={pagePath}
              itemPath={`${itemPath}/${cqItemsOrder[index]}`}
              columnCount="12"
              gridClassNames={''}
              config={{
                isEmpty: () => true,
                resourceType: 'mi-aem-common-spa/components/container',
              }}
            />
          </div>
        ))}
      </>
    );
  };

  const PublishComponents = () => {
    return (
      <>
        {cqItemsOrder?.map((cardName: string, index: number) => {
          if (Object?.prototype?.hasOwnProperty?.call(cqItems, cardName)) {
            const card = cqItems?.[cardName] as Record<string, unknown>;
            const gridUtil = respGridUtil({ ...card });
            return (
              <div
                key={`${cardName}+${index}`}
                className={clsx('container-component', {
                  'invisible ':
                    !isTargetContentLoaded && (String(enableOfferVariation) === 'false' || !enableOfferVariation),
                })}
              >
                <ResponsiveGrid {...gridUtil} />
              </div>
            );
          } else return <></>;
        })}
      </>
    );
  };

  return (
    <StyledMerchandisingDCAContainer
      className={clsx('merchandising-dca-container m-container-fullbleed', styleclass, {
        'skeleton-loader-composite ':
          !isTargetContentLoaded &&
          !isAuthorMode &&
          (String(enableOfferVariation) === 'false' || !enableOfferVariation),
        'showOnlyWithDCAContent ': hideByDefault === true,
        'showComponent ': hideByDefault === false,
      })}
    >
      {isAuthorMode ? <AuthorComponents /> : <PublishComponents />}
    </StyledMerchandisingDCAContainer>
  );
};

const ABTestWrapper: FC<IMerchandisingDCAContainerProps> = props => {
  const { targetMbox } = props;
  const updatedPageModel = useDCAPageModelStore(state => state.pageModel) as IUpdatedPageModel;

  const [model, setModel] = useState(props);
  const [isPropsUpdated, setIsPropsUpdated] = useState(false);

  useEffect(() => {
    if (targetMbox) {
      if (updatedPageModel?.alloyModelJSON?.[targetMbox] && !isPropsUpdated) {
        const updatedModel = {
          ...updatedPageModel?.alloyModelJSON?.[targetMbox],
          isTargetContentLoaded: true,
        } as Record<string, unknown>;
        setModel(respGridUtil(updatedModel));
        setIsPropsUpdated(true);
      } else if (updatedPageModel?.alloyModelJSON?.[targetMbox] === null && !isPropsUpdated) {
        let hideByDefaultValue = false;
        if (updatedPageModel?.alloyModelJSON?.[targetMbox + '_receipe']) {
          const recipeData = updatedPageModel?.alloyModelJSON?.[targetMbox + '_receipe'] as Record<string, unknown>;
          // Extract hideByDefault from recipe data
          if ('hideByDefault' in recipeData) {
            hideByDefaultValue = (recipeData as Record<string, unknown>)['hideByDefault'] as boolean;
          }
        }

        setModel({ ...model, isTargetContentLoaded: true, hideByDefault: hideByDefaultValue });
        setIsPropsUpdated(true);
      }
    } else {
      if (!isPropsUpdated) {
        setModel({ ...model, isTargetContentLoaded: true });
        setIsPropsUpdated(true);
      }
    }
  }, [updatedPageModel, targetMbox, model]);

  useEffect(() => {
    if (model?.isAuthorMode) {
      setModel({ ...props });
    }
  }, [props]);

  return useMemo(() => <DCAContainer {...model} />, [model]);
};

const Config = {
  emptyLabel: 'DCA A/B Test Wrapper',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/dcaabtestwrapper`,
};

export const MerchandisingDCAContainer: FC<IMerchandisingDCAContainerProps> = props => (
  <EditableComponent config={Config} {...props}>
    <ABTestWrapper {...props} />
  </EditableComponent>
);
